import { CHILD_BENEFIT_TYPES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';

import { generateArrayCommands } from './generateArrayCommands';
import type { SchemaLivingSituationForm } from '../schemas/living.schema';

const generateLivingSituationCommandsPerApplicant = ({
    applicantId,
    newData,
    oldData,
}: {
    oldData: SchemaLivingSituationForm['antragsteller1'] | SchemaLivingSituationForm['antragsteller2'];
    newData: SchemaLivingSituationForm['antragsteller1'] | SchemaLivingSituationForm['antragsteller2'];
    applicantId: string;
}) => {
    const commands: Array<{ commandName: string; payload: Record<string, unknown> }> = [];
    if (
        oldData?.wohnsituation?.anzahlPersonenImHaushalt !== newData?.wohnsituation?.anzahlPersonenImHaushalt ||
        oldData?.wohnsituation?.wohnart !== newData?.wohnsituation?.wohnart ||
        oldData?.wohnsituation?.gemeinsamerHaushalt !== newData?.wohnsituation?.gemeinsamerHaushalt
    ) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.changeApplicantLivingSituation,
            payload: {
                anzahlPersonenImHaushalt: newData?.wohnsituation?.anzahlPersonenImHaushalt,
                applicantId,
                gemeinsamerHaushalt: newData?.wohnsituation?.gemeinsamerHaushalt,
                wohnart: newData?.wohnsituation?.wohnart,
            },
        });
    }
    if (
        (oldData?.wohnsituation?.anschrift !== undefined ||
            newData?.wohnsituation?.anschrift?.hausnummer != null ||
            newData?.wohnsituation?.anschrift?.plz != null ||
            newData?.wohnsituation?.anschrift?.strasse != null ||
            newData?.wohnsituation?.anschrift?.ort != null ||
            newData?.wohnsituation?.anschrift?.land != null ||
            newData?.wohnsituation?.anschrift?.wohnhaftSeit != null) &&
        (oldData?.wohnsituation?.anschrift?.hausnummer !== newData?.wohnsituation?.anschrift?.hausnummer ||
            oldData?.wohnsituation?.anschrift?.plz !== newData?.wohnsituation?.anschrift?.plz ||
            oldData?.wohnsituation?.anschrift?.strasse !== newData?.wohnsituation?.anschrift?.strasse ||
            oldData?.wohnsituation?.anschrift?.ort !== newData?.wohnsituation?.anschrift?.ort ||
            oldData?.wohnsituation?.anschrift?.land !== newData?.wohnsituation?.anschrift?.land ||
            oldData?.wohnsituation?.anschrift?.wohnhaftSeit?.getTime() !==
                newData?.wohnsituation?.anschrift?.wohnhaftSeit?.getTime())
    ) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.changeApplicantAddress,
            payload: {
                applicantId,
                hausnummer: newData?.wohnsituation?.anschrift?.hausnummer,
                land: newData?.wohnsituation?.anschrift?.land,
                ort: newData?.wohnsituation?.anschrift?.ort,
                plz: newData?.wohnsituation?.anschrift?.plz,
                strasse: newData?.wohnsituation?.anschrift?.strasse,
                wohnhaftSeit: newData?.wohnsituation?.anschrift?.wohnhaftSeit,
            },
        });
    }
    if (
        (oldData?.wohnsituation?.vorherigeAnschrift !== undefined ||
            newData?.wohnsituation?.vorherigeAnschrift?.hausnummer != null ||
            newData?.wohnsituation?.vorherigeAnschrift?.land != null ||
            newData?.wohnsituation?.vorherigeAnschrift?.ort != null ||
            newData?.wohnsituation?.vorherigeAnschrift?.plz != null ||
            newData?.wohnsituation?.vorherigeAnschrift?.strasse != null ||
            newData?.wohnsituation?.vorherigeAnschrift?.wohnhaftSeit != null) &&
        (oldData?.wohnsituation?.vorherigeAnschrift?.hausnummer !==
            newData?.wohnsituation?.vorherigeAnschrift?.hausnummer ||
            oldData?.wohnsituation?.vorherigeAnschrift?.plz !== newData?.wohnsituation?.vorherigeAnschrift?.plz ||
            oldData?.wohnsituation?.vorherigeAnschrift?.strasse !==
                newData?.wohnsituation?.vorherigeAnschrift?.strasse ||
            oldData?.wohnsituation?.vorherigeAnschrift?.ort !== newData?.wohnsituation?.vorherigeAnschrift?.ort ||
            oldData?.wohnsituation?.vorherigeAnschrift?.land !== newData?.wohnsituation?.vorherigeAnschrift?.land ||
            oldData?.wohnsituation?.vorherigeAnschrift?.wohnhaftSeit?.getTime() !==
                newData?.wohnsituation?.vorherigeAnschrift?.wohnhaftSeit?.getTime())
    ) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.changeApplicantPreviousAddress,
            payload: {
                applicantId,
                hausnummer: newData?.wohnsituation?.vorherigeAnschrift?.hausnummer,
                land: newData?.wohnsituation?.vorherigeAnschrift?.land,
                ort: newData?.wohnsituation?.vorherigeAnschrift?.ort,
                plz: newData?.wohnsituation?.vorherigeAnschrift?.plz,
                strasse: newData?.wohnsituation?.vorherigeAnschrift?.strasse,
                wohnhaftSeit: newData?.wohnsituation?.vorherigeAnschrift?.wohnhaftSeit,
            },
        });
    }
    if (oldData?.familienstand !== newData?.familienstand) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.changeApplicantMaritalStatus,
            payload: {
                applicantId,
                familienstand: newData?.familienstand,
            },
        });
    }
    return commands;
};

const generatePkwCommands = ({
    antragsteller,
    applicantId,
    newData,
    oldData,
}: {
    antragsteller: 1 | 2;
    applicantId?: string;
    oldData: SchemaLivingSituationForm;
    newData: SchemaLivingSituationForm;
}) => {
    const commands: Array<{ commandName: string; payload: Record<string, unknown> }> = [];

    const oldDataPkwAntragsteller = oldData[`antragsteller${antragsteller}`]?.wohnsituation?.anzahlPkw;
    const newDataPkwAntragsteller = newData[`antragsteller${antragsteller}`]?.wohnsituation?.anzahlPkw;
    if (oldDataPkwAntragsteller !== newDataPkwAntragsteller) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.changeCar,
            payload: {
                anzahlPkw: newDataPkwAntragsteller,
                applicantId,
            },
        });
    }
    return commands;
};

const generateLivingSituationCommands = ({
    applicantId1,
    applicantId2,
    newData,
    oldData,
}: {
    oldData: SchemaLivingSituationForm;
    newData: SchemaLivingSituationForm;
    applicantId1: string;
    applicantId2?: string;
}) => {
    const commands: Array<{ commandName: string; payload: Record<string, unknown> }> = [];

    commands.push(
        ...generateLivingSituationCommandsPerApplicant({
            applicantId: applicantId1,
            newData: newData.antragsteller1,
            oldData: oldData.antragsteller1,
        }),
    );
    if (oldData.antragsteller2 && newData.antragsteller2 && applicantId2) {
        commands.push(
            ...generateLivingSituationCommandsPerApplicant({
                applicantId: applicantId2,
                newData: newData.antragsteller2,
                oldData: oldData.antragsteller2,
            }),
        );
    }

    commands.push(...generatePkwCommands({ antragsteller: 1, applicantId: applicantId1, newData, oldData }));
    commands.push(...generatePkwCommands({ antragsteller: 2, applicantId: applicantId2, newData, oldData }));
    if (newData.kinder) {
        // both sides get the form default, so an untouched child is not a change
        const withDefaultBenefitType = (children: NonNullable<SchemaLivingSituationForm['kinder']>) =>
            children.map((child) => ({
                ...child,
                kindergeldFuer: child.kindergeldHoehe
                    ? child.kindergeldFuer ?? CHILD_BENEFIT_TYPES.ERSTES_ODER_ZWEITES_KIND
                    : null,
            }));
        commands.push(
            ...generateArrayCommands({
                itemName: 'Child',
                newData: withDefaultBenefitType(newData.kinder),
                oldData: withDefaultBenefitType(oldData.kinder ?? []),
            }),
        );
    }

    return commands;
};

export { generateLivingSituationCommands };
